/**
 * Editor Layout
 *
 * Minimal layout for full-page editors (signing, document editing, etc.)
 * - No sidebar
 * - Simple header with back navigation
 * - Full viewport for content
 */

import { Outlet, useNavigate, useParams, useSearchParams, Link } from "react-router-dom"
import { ArrowLeft, X } from "@phosphor-icons/react"
import { Button } from "../ui/button"
import { useAuthStore } from "../../stores"
import { useEffect } from "react"
import { keys } from "../../keys"

export function EditorLayout() {
  const { user, isAuthenticated, isInitialized } = useAuthStore()
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId?: string }>()
  const [searchParams] = useSearchParams()

  // Redirect to auth if not logged in (only after auth check is complete)
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      // Redirect to the auth app for login
      window.location.href = `${keys.AUTH_URL}?redirect=${encodeURIComponent(window.location.href)}`
    }
  }, [isInitialized, isAuthenticated])

  // Show loading while checking auth or if no user yet
  if (!isInitialized || !user) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  const hasMatter = matterId && matterId !== "new"
  const workflowId = searchParams.get("workflow")
  const adminBackUrl = workflowId
    ? `${keys.ADMIN_URL}/editor/workflows/${workflowId}/signing`
    : undefined

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Minimal header */}
      <header className="flex h-14 items-center justify-between border-b bg-card px-4">
        <div className="flex items-center gap-3">
          {hasMatter ? (
            <Link
              to={`/matters/${matterId}/documents/signing`}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Matter</span>
            </Link>
          ) : adminBackUrl ? (
            <a
              href={adminBackUrl}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Templates</span>
            </a>
          ) : (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {adminBackUrl ? (
            <a href={adminBackUrl} title="Close editor">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </a>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={hasMatter ? () => navigate(`/matters/${matterId}/documents/signing`) : () => navigate(-1)}
              className="h-8 w-8"
              title="Close editor"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </header>

      {/* Full-height content area */}
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
